/**
 * Tragfähigkeitslücke = BG-Bedarf − anzurechnendes Einkommen.
 *
 * Positiver Wert = ungedeckte Lücke (Bürgergeld nötig).
 * Negativer Wert = Überdeckung (theoretisch keine Hilfebedürftigkeit mehr).
 * 0 = Punktlandung.
 *
 * Die Lücke ist die zentrale Eingabe in den Private-Score (Konzept §4.2
 * Dimension 6) und Hauptindikator für die formelle Stellungnahme.
 */
import type { MoneyCents } from '../schema/brands.js';
import type { BgComposition } from '../schema/bgComposition.js';
import { computeBgBedarf, type BgBedarfBreakdown } from './computeBgBedarf.js';
import {
  computeAnzurechnendesEinkommen,
  type AnzurechnendesEinkommenBreakdown,
} from './computeAnzurechnendesEinkommen.js';

export interface TragfaehigkeitsLueckeBreakdown {
  bedarf: BgBedarfBreakdown;
  einkommen: AnzurechnendesEinkommenBreakdown;
  /** Positive Cents = ungedeckte Lücke; negative = Überdeckung. */
  luckeCents: MoneyCents;
}

export interface TragfaehigkeitsLueckeInput {
  bg: BgComposition;
  bruttoErwerbseinkommenCents: MoneyCents;
  /**
   * Sonstiges anrechenbares Einkommen der BG pro Monat (Kindergeld, Elterngeld,
   * Unterhalt, Renten). Voll angerechnet, ohne Erwerbstätigen-Freibetrag. Default 0.
   */
  sonstigesEinkommenCents?: MoneyCents;
}

export function computeTragfaehigkeitsLuecke(
  input: TragfaehigkeitsLueckeInput,
): TragfaehigkeitsLueckeBreakdown {
  const bedarf = computeBgBedarf(input.bg);
  const hasChildrenInBg = input.bg.members.some((m) => m.role === 'kind');
  const einkommen = computeAnzurechnendesEinkommen({
    bruttoErwerbseinkommenCents: input.bruttoErwerbseinkommenCents,
    hasChildrenInBg,
    ...(input.sonstigesEinkommenCents !== undefined
      ? { sonstigesEinkommenCents: input.sonstigesEinkommenCents }
      : {}),
  });

  const luckeCents = ((bedarf.totalBedarfCents as number) -
    (einkommen.anzurechnendesEinkommenCents as number)) as MoneyCents;

  return { bedarf, einkommen, luckeCents };
}
